<template>
    <BaseDialog
        :open="open"
        :disable-close-on-backdrop-click="isSaving"
        title="Set your time zone"
        dialog-content-class="p-5 sm:p-6 md:!max-w-md"
        @update:open="emit('update:open', $event)">
        <form class="flex w-full flex-col gap-6" @submit.prevent="saveTimezone">
            <div class="flex flex-col gap-2">
                <h2 class="title-subsection">Set your time zone</h2>
                <p class="body-small text-valence-grey-750">Set your time zone to get the full Development Journeys experience.</p>
            </div>

            <label class="flex flex-col gap-2" for="development-journeys-timezone">
                <span class="body-small font-medium text-valence-grey-850">Time zone</span>
                <BaseCombobox
                    v-model="selectedTimezone"
                    :disabled="isSaving"
                    :options="timezoneOptions"
                    input-id="development-journeys-timezone"
                    placeholder="Select a time zone"
                    auto-focus />
            </label>

            <span role="alert" aria-atomic="true" :class="saveError ? 'body-small text-red-700' : 'sr-only'">{{ saveError }}</span>

            <BaseButton
                type="submit"
                theme="primary"
                :disabled="!selectedTimezone || isSaving"
                data-testid="save-development-journeys-timezone"
                class="min-h-10 rounded-lg bg-valence-stone-850 hover:bg-valence-stone-900 disabled:bg-valence-cream-300 disabled:text-valence-cream-400">
                {{ isSaving ? "Saving…" : "Save and continue" }}
            </BaseButton>
        </form>
    </BaseDialog>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCombobox from "~vue/components/BaseCombobox.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import { computed, ref, watch } from "vue";

const props = defineProps<{
    open: boolean;
    timezones: string[];
    timezone?: string;
}>();

const emit = defineEmits<{
    saved: [];
    "update:open": [open: boolean];
}>();

type TimezoneOption = {
    label: string;
    value: string;
};

const timezoneOptions = computed<TimezoneOption[]>(() => props.timezones.map((timezone) => ({ label: timezone, value: timezone })));
const selectedTimezone = ref<TimezoneOption | null>(null);
const saveError = ref<string | null>(null);
const timezonePayload = computed(() => ({ timezone: selectedTimezone.value?.value }));
const { error: timezoneUpdateError, execute: updateTimezone, isFetching: isSaving } = useFetch("/api/accounts/user", { immediate: false }).put(timezonePayload).json();

watch(
    () => [props.open, props.timezone],
    ([isOpen, timezone]) => {
        if (isOpen) {
            selectedTimezone.value = timezoneOptions.value.find((option) => option.value === timezone) ?? null;
        }
    },
    { immediate: true },
);

const saveTimezone = async () => {
    if (!selectedTimezone.value || isSaving.value) {
        return;
    }

    saveError.value = null;
    await updateTimezone();

    if (timezoneUpdateError.value) {
        saveError.value = "We couldn't save your time zone. Please try again.";
        return;
    }

    emit("saved");
};
</script>
